// vg-binding: P7.D-148
// vg-binding: P7.D-149
// vg-binding: P18.D-21
// vg-binding: P18.D-28
//
// apps/vendor/src/features/orders/api.ts
//
// TanStack-Query surface wrapping the Wave 5 BE orders endpoints.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Wire types — mirror BE shape
// ---------------------------------------------------------------------------

export type OrderStatus =
  | 'assigned'
  | 'accepted'
  | 'in_production'
  | 'quality_check'
  | 'ready_to_ship'
  | 'shipped'
  | 'pending_reassignment'
  | 'completed';

export type SlaColorTier = 'green' | 'yellow' | 'orange' | 'red';

export interface OrdersListItem {
  assignment_id: string;
  order_id: string;
  /** Printway order number (e.g. PWO...) for the grouped order header. */
  order_number?: string | null;
  item_id: string;
  vendor_id: string;
  /** Variant SKU for the line-item sub-row. */
  sku?: string | null;
  /** Artwork presence for the line-item sub-row. */
  artwork_status?: 'ok' | 'missing';
  /** Vendor cost in cents (vendor-facing — NOT merchant selling price). */
  vendor_cost_cents?: number;
  status: OrderStatus;
  assigned_at: number;
  sla_deadline: number;
  effective_deadline: number;
  sla_percent_used: number;
  sla_color_tier: SlaColorTier;
  back_order_enabled: boolean;
  back_order_used: boolean;
  item_version: number;
  // R3-9 (2026-05-16) — human-readable item label + qty.
  product_name?: string | null;
  quantity?: number;
  // Shipping label snapshot surfaced on the list (null until a label is
  // bought) — drives the Tracking + Label columns.
  tracking_number?: string | null;
  label_url?: string | null;
  // Amendment #7 — backing data; the Assize column is removed in
  // amend-9 (folded into vendor_status). Kept for back-compat.
  is_assized?: boolean;
  // Amendment #9 — unified vendor-facing status (single Status
  // column): new | assized | labeled | shipped (more later).
  vendor_status?: string;
}

export interface OrdersListData {
  rows: OrdersListItem[];
  meta: {
    next_cursor: string | null;
    total_returned: number;
    /** Total assignments (independent of page). BR-PAGING numbered nav. */
    total: number;
    /** 1-based current page. */
    page: number;
    /** Total page count. */
    page_count: number;
    /** Page size echoed back. */
    limit: number;
    /**
     * dbg-20260704 — per-status counts for the tab strip, independent of the
     * selected tab. `all` = sum. Keyed by status; drives hide-tab-at-0.
     */
    counts?: Record<string, number>;
  };
}

/** One downloadable artwork asset for an order item (fix/vendor-batch-e). */
export interface ArtworkFile {
  /** Print area the file belongs to (e.g. "front", "back"). */
  area: string;
  /** Display filename. */
  name: string;
  /** Direct download URL. */
  url: string;
}

export interface OrderItemDetail {
  assignment_id: string;
  order_id: string;
  item_id: string;
  vendor_id: string;
  status: OrderStatus;
  assigned_at: number;
  accepted_at: number | null;
  sla_deadline: number;
  effective_deadline: number;
  sla_percent_used: number;
  sla_color_tier: SlaColorTier;
  back_order_enabled: boolean;
  back_order_used: boolean;
  back_order_used_at: number | null;
  item_version: number;
  order_summary: {
    order_number: string | null;
    quantity: number | null;
    product_name: string | null;
    variant_label: string | null;
    recipient_name: string | null;
    ship_to: {
      line1: string | null;
      line2: string | null;
      city: string | null;
      state: string | null;
      postal: string | null;
      country: string | null;
    } | null;
  };
  /**
   * Downloadable artwork assets. OPTIONAL — older BE builds omit the field;
   * the FE must tolerate absence (ArtworkPanel falls back to its placeholder).
   */
  artwork_files?: ArtworkFile[];
  /**
   * When the vendor's OWN system acknowledged this assignment through the
   * machine lane (`POST /api/v1/vendor-api/assignments/:id/ack`, task-21 —
   * `vendor_assignments.external_ack_at`, set once).
   *
   * OPTIONAL and tolerated-absent on purpose: the portal detail endpoint does
   * not project the column yet (task-21 added it to the model + the machine
   * lane only). Typing it here means the portal renders it the moment the read
   * carries it, and until then `OrderInfo` falls back to the machine rows in
   * the transition log — which is real evidence — instead of printing a
   * timestamp nobody sent.
   */
  external_ack_at?: number | null;
}

/**
 * Who moved the row. Phase 18 (P18.D-01) added a MACHINE principal: a write
 * that arrived on `/api/v1/vendor-api/*` is audited as the API key, never as a
 * person, so `vendor_api` is a peer of the three human/system lanes rather than
 * a flavour of `system`.
 *
 * Mirrors `OrderItemTransitionRow['transitioned_by_role']` in
 * `apps/api/src/modules/vendor/services/orderDetailService.ts` (task-21).
 */
export type TransitionActorRole =
  | 'vendor_staff'
  | 'admin_override'
  | 'system'
  | 'vendor_api';

export interface TransitionRow {
  event_id: string;
  order_id: string;
  item_id: string;
  from_state: string;
  to_state: string;
  from_version: number;
  to_version: number;
  reason: string | null;
  metadata: Record<string, unknown>;
  occurred_at: number;
  /**
   * NULL on a machine-lane row.
   *
   * task-03 widened the audit column to nullable and task-21 widened the BE
   * response type to `string | null`; this declaration kept claiming `string`,
   * so the first component to read the id would have dereferenced null with a
   * green typecheck behind it — the same shape of bug that crashed
   * `orderDetailService` before task-21 guarded it. Read `transitioned_by_role`
   * to tell the lanes apart; P18.D-01 forbids naming a person for a machine
   * action, so there is no id to fall back to.
   */
  transitioned_by_staff_id: string | null;
  transitioned_by_role: TransitionActorRole;
}

interface ApiOk<T> { ok: true; data: T }
interface ApiErr {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    current_state?: string;
    current_version?: number;
  };
}

export class OrdersApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly currentState: string | undefined;
  public readonly currentVersion: number | undefined;
  public readonly userMessage: string | undefined;
  constructor(
    code: string,
    message: string,
    statusCode: number,
    extras?: { current_state?: string; current_version?: number; user_message?: string },
  ) {
    super(message);
    this.name = 'OrdersApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.currentState = extras?.current_state;
    this.currentVersion = extras?.current_version;
    this.userMessage = extras?.user_message;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as ApiOk<T> | ApiErr | null;
  if (!body) {
    throw new OrdersApiError('NETWORK', 'Empty response body', res.status);
  }
  if (!('ok' in body) || body.ok !== true) {
    const err = (body as ApiErr).error;
    throw new OrdersApiError(err?.code ?? 'UNKNOWN', err?.message ?? 'Request failed', res.status, {
      current_state: err?.current_state,
      current_version: err?.current_version,
      user_message: err?.user_message,
    });
  }
  return body.data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const ORDERS_LIST_KEY = ['vendor', 'orders', 'list'] as const;
const ORDERS_DETAIL_KEY = ['vendor', 'orders', 'detail'] as const;
const ORDERS_TRANSITIONS_KEY = ['vendor', 'orders', 'transitions'] as const;

export interface OrdersListParams {
  status?: OrderStatus | 'all';
  /** DEPRECATED — prefer `page`. Kept for back-compat callers. */
  cursor?: string;
  /** 1-based page (numbered pagination per BR-PAGING). */
  page?: number;
  limit?: number;
  sort?: 'sla_asc' | 'sla_desc' | 'assigned_asc';
  /** Free-text search — server matches against order_id hex prefix. */
  q?: string;
  // Amendment #9 — list filters.
  /** Filter by unified vendor status (new|assized|labeled|shipped). */
  vendor_status?: string;
  /** Search by order name / order number (case-insensitive substring). */
  q_name?: string;
  /** Assign-time range (epoch ms). */
  assigned_from?: number;
  assigned_to?: number;
}

export function useOrdersList(
  params: OrdersListParams,
): UseQueryResult<OrdersListData, OrdersApiError> {
  return useQuery<OrdersListData, OrdersApiError>({
    queryKey: [...ORDERS_LIST_KEY, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status && params.status !== 'all') qs.set('status', params.status);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.sort) qs.set('sort', params.sort);
      if (params.q && params.q.trim().length > 0) qs.set('q', params.q.trim());
      // Amendment #9 — vendor_status + name search + assign range.
      if (params.vendor_status) qs.set('vendor_status', params.vendor_status);
      if (params.q_name && params.q_name.trim().length > 0) qs.set('q_name', params.q_name.trim());
      if (typeof params.assigned_from === 'number') qs.set('assigned_from', String(params.assigned_from));
      if (typeof params.assigned_to === 'number') qs.set('assigned_to', String(params.assigned_to));
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/orders${qstr ? `?${qstr}` : ''}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<OrdersListData>(res);
    },
    refetchInterval: 30 * 1000,
  });
}

export function useOrderDetail(
  order_id: string | undefined,
  item_id: string | undefined,
): UseQueryResult<OrderItemDetail, OrdersApiError> {
  return useQuery<OrderItemDetail, OrdersApiError>({
    queryKey: [...ORDERS_DETAIL_KEY, order_id, item_id],
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${order_id}/items/${item_id}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<OrderItemDetail>(res);
    },
    enabled: Boolean(order_id && item_id),
    refetchInterval: 30 * 1000,
  });
}

export function useOrderTransitions(
  order_id: string | undefined,
  item_id: string | undefined,
): UseQueryResult<{ rows: TransitionRow[]; next_cursor: string | null }, OrdersApiError> {
  return useQuery<{ rows: TransitionRow[]; next_cursor: string | null }, OrdersApiError>({
    queryKey: [...ORDERS_TRANSITIONS_KEY, order_id, item_id],
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${order_id}/items/${item_id}/transitions`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<{ rows: TransitionRow[]; next_cursor: string | null }>(res);
    },
    enabled: Boolean(order_id && item_id),
  });
}

// R13 — append-only printer-interval audit log per order item.
export interface ProductionHistoryRow {
  id: string;
  printer_id: string;
  printer_name: string;
  printer_type: string;
  started_at: number;
  ended_at: number | null;
  duration_ms: number | null;
  ended_reason: 'completed' | 'shipped' | 'reassigned' | 'rejected' | 'cancelled' | null;
  notes: string | null;
}

const ORDERS_PRODUCTION_HISTORY_KEY = ['vendor', 'orders', 'production-history'] as const;

export function useProductionHistory(
  order_id: string | undefined,
  item_id: string | undefined,
): UseQueryResult<{ rows: ProductionHistoryRow[] }, OrdersApiError> {
  return useQuery<{ rows: ProductionHistoryRow[] }, OrdersApiError>({
    queryKey: [...ORDERS_PRODUCTION_HISTORY_KEY, order_id, item_id],
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${order_id}/items/${item_id}/production-history`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<{ rows: ProductionHistoryRow[] }>(res);
    },
    enabled: Boolean(order_id && item_id),
  });
}

export interface TransitionInput {
  order_id: string;
  item_id: string;
  item_version: number;
  to_state: OrderStatus;
  reason?: string;
  metadata?: Record<string, unknown>;
}

export function useTransitionItem(): UseMutationResult<
  { event_id: string; to_version: number; to_state: OrderStatus },
  OrdersApiError,
  TransitionInput
> {
  const qc = useQueryClient();
  return useMutation<
    { event_id: string; to_version: number; to_state: OrderStatus },
    OrdersApiError,
    TransitionInput
  >({
    mutationFn: async (input) => {
      const body: Record<string, unknown> = { to_state: input.to_state };
      if (input.reason) body.reason = input.reason;
      if (input.metadata) body.metadata = input.metadata;
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${input.order_id}/items/${input.item_id}/transition`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'If-Match': String(input.item_version),
          },
          body: JSON.stringify(body),
        },
      );
      return unwrap(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_DETAIL_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_TRANSITIONS_KEY });
      // R4-1 (2026-05-16): print queue shows item_status — refresh it too
      // so the next-state button re-derives after a transition.
      void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
      // R13 — terminal transitions close production-history rows.
      void qc.invalidateQueries({
        queryKey: ['vendor', 'orders', 'production-history'],
      });
    },
    onError: () => {
      // R4-1: stale-state errors (current_state mismatch) mean the local
      // cache is behind. Refetch so the user sees the canonical state and
      // the button label re-derives.
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_DETAIL_KEY });
      void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
      // P18.D-28 / E-43 — when the conflicting write came from the vendor's own
      // machine lane, the row explaining it is in the transition log. Refetching
      // that too turns "your action failed" into "here is what moved it".
      void qc.invalidateQueries({ queryKey: ORDERS_TRANSITIONS_KEY });
    },
  });
}

export interface BackOrderInput {
  order_id: string;
  item_id: string;
  item_version: number;
  reason?: string;
}

export function useBackOrder(): UseMutationResult<
  { to_version: number; back_order_used_at: number },
  OrdersApiError,
  BackOrderInput
> {
  const qc = useQueryClient();
  return useMutation<
    { to_version: number; back_order_used_at: number },
    OrdersApiError,
    BackOrderInput
  >({
    mutationFn: async (input) => {
      const body: Record<string, unknown> = {};
      if (input.reason) body.reason = input.reason;
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${input.order_id}/items/${input.item_id}/back-order`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'If-Match': String(input.item_version),
          },
          body: JSON.stringify(body),
        },
      );
      return unwrap(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_DETAIL_KEY });
    },
    // P18.D-28 / E-19 / E-43 — back-order is an If-Match write like every other
    // portal mutation, and the machine lane can advance the same row between
    // the 30s poll and the click. A 409 CONFLICT_VERSION means our
    // `item_version` is stale, i.e. the cache is behind the server, so the cure
    // is a refetch. This hook previously had NO onError at all: the mutation
    // rejected, ActionBar printed the message, and the version the next click
    // would send stayed just as stale (silent-failure ratchet
    // `printway/require-mutation-on-error`).
    onError: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_DETAIL_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_TRANSITIONS_KEY });
    },
  });
}

export interface BulkTransitionInput {
  to_state: OrderStatus;
  items: Array<{
    item_id: string;
    order_id: string;
    expected_version: number;
    reason?: string;
    tracking?: string;
    qc_pass?: boolean;
  }>;
}

export function useBulkTransition(): UseMutationResult<
  {
    success: Array<{ item_id: string; to_version: number; event_id: string }>;
    failed: Array<{ item_id: string; error_code: string; message: string }>;
  },
  OrdersApiError,
  BulkTransitionInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkTransitionInput) => {
      const res = await csrfFetch('/api/v1/vendor/orders/bulk-transition', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<{
        success: Array<{ item_id: string; to_version: number; event_id: string }>;
        failed: Array<{ item_id: string; error_code: string; message: string }>;
      }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Amendment #7 (253/254) — Assize packaging dimensions
// ---------------------------------------------------------------------------

export interface PackagingDims {
  l: number;
  w: number;
  h: number;
  weightGrams: number;
}

export interface AssizeItemInput {
  orderId: string;
  itemId: string;
  dims: PackagingDims;
}

/** PUT per-item packaging dimensions (Assize modal persist). */
export function useAssizeItem(): UseMutationResult<
  { orderId: string; itemId: string; packagingDimensions: PackagingDims },
  OrdersApiError,
  AssizeItemInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssizeItemInput) => {
      const res = await csrfFetch(
        `/api/v1/vendor/orders/${input.orderId}/items/${input.itemId}/packaging-dimensions`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input.dims),
        },
      );
      return unwrap<{
        orderId: string;
        itemId: string;
        packagingDimensions: PackagingDims;
      }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ORDERS_DETAIL_KEY });
    },
  });
}

export interface AssizeImportResult {
  imported: number;
  failed: number;
  errors: Array<{ row: number; field: string; reason: string }>;
}

/** POST filled Bulk Assize template rows → batch update dims. */
export function useAssizeImport(): UseMutationResult<
  AssizeImportResult,
  OrdersApiError,
  Array<Record<string, unknown> & { row: number }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: Array<Record<string, unknown> & { row: number }>) => {
      const res = await csrfFetch('/api/v1/vendor/orders/assize-import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      return unwrap<AssizeImportResult>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
    },
  });
}

/**
 * Build the Bulk Assize export URL. TH1: pass item_ids (selected
 * rows). TH2: pass the current list filter (status/q). The browser GETs this
 * with credentials to download the CSV template.
 */
export function assizeExportUrl(params: {
  item_ids?: string[];
  status?: string;
  q?: string;
}): string {
  const qs = new URLSearchParams();
  if (params.item_ids && params.item_ids.length > 0) {
    qs.set('item_ids', params.item_ids.join(','));
  } else {
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
  }
  const suffix = qs.toString();
  // UAT #2045 — this URL is opened via window.open (not csrfFetch), so the
  // VITE_API_BASE_URL rewrite inside csrfFetch never applies. On sandbox
  // the vendor SPA origin does not proxy /api → the relative URL fell
  // through to the SPA shell (tester saw 404). Prepend the API base.
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  return `${base}/api/v1/vendor/orders/assize-export${suffix ? `?${suffix}` : ''}`;
}

/**
 * UAT #2042 — label viewer URL. The row's `label_url` is a stateless snapshot
 * pointer (mock-storage / cdn.example on seed) that the browser cannot load,
 * so the iframe rendered "server không đọc được label". The API instead
 * rebuilds + streams the PDF on demand at `/labels/:assignmentId/pdf`. Same
 * VITE_API_BASE_URL rule as assizeExportUrl (loaded via iframe/window.open,
 * not csrfFetch, so the base must be prepended here).
 */
export function labelPdfUrl(assignmentId: string): string {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  return `${base}/api/v1/vendor/labels/${assignmentId}/pdf`;
}

// ---------------------------------------------------------------------------
// Amendment #8 — scan a labeled order to shipped (warehouse handover)
// ---------------------------------------------------------------------------

export interface ScanTransferResult {
  orderId: string;
  itemId: string;
  vendorStatus: string;
  handoverAt: number;
  alreadyShipped: boolean;
}

/** POST a scanned barcode → promote labeled order to shipped (handover). */
export function useScanTransfer(): UseMutationResult<
  ScanTransferResult,
  OrdersApiError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (barcode: string) => {
      const res = await csrfFetch('/api/v1/vendor/orders/scan-transfer', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ barcode }),
      });
      return unwrap<ScanTransferResult>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORDERS_LIST_KEY });
    },
  });
}
